/**
 * Cover art loading via Tauri IPC — bypasses mixed-content blocking.
 *
 * WKWebView blocks `<img src="http://127.0.0.1:…">` on HTTPS pages.
 * Tauri IPC (`invoke()`) doesn't go through HTTP at all, so cover art
 * read from the on-disk cache arrives as base64 over the JS bridge.
 *
 * Two strategies:
 *   1. By hash — reads from cover_cache, then local-core/artwork/
 *   2. By folder path — extracts on-demand from audio files / sidecar images
 *      (only real filesystem paths — never stable ids like local:album:…)
 */

import { isConnectorAvailable } from './connector';
import { isHomeLibraryHost, isRemoteConnectorShell } from '../services/localLibraryIpcGate';

interface CoverArtResponse {
  data: string;
  mime_type: string;
}

const _cache = new Map<string, string | null>();
const _pathCache = new Map<string, string | null>();
const _inflight = new Map<string, Promise<string | null>>();

const HASH_RE = /^[0-9a-f]{64}$/;

/** Normalize artwork hash from API (lowercase 64-char hex). */
export function normalizeCoverHash(hash: string | null | undefined): string | null {
  if (!hash || typeof hash !== 'string') return null;
  const h = hash.trim().toLowerCase();
  return HASH_RE.test(h) ? h : null;
}

/** Real album folder on disk — not `local:album:…` stable ids. */
export function pickAlbumFolderPath(album: {
  library_folder_path?: string | null;
  sample_file_path?: string | null;
  file_path?: string | null;
} | null | undefined): string | null {
  if (!album) return null;
  const explicit = album.library_folder_path;
  if (explicit && typeof explicit === 'string' && !explicit.startsWith('local:')) {
    return explicit;
  }
  const sample = album.sample_file_path || album.file_path;
  if (sample && typeof sample === 'string' && !sample.startsWith('local:')) {
    const idx = Math.max(sample.lastIndexOf('/'), sample.lastIndexOf('\\'));
    if (idx > 0) return sample.slice(0, idx);
  }
  return null;
}

function isLocalLibraryAlbum(album: { isLocalLibrary?: boolean; __source?: string; machine_id?: string } | null | undefined): boolean {
  if (!album) return false;
  if (album.__source === 'remote_tunnel') return false;
  return !!(album.isLocalLibrary || album.__source === 'local_library');
}

/** Tauri Connector viewing a home library elsewhere (no local SQLite catalog). */
export function isRemoteCoverViewerShell(): boolean {
  if (typeof window === 'undefined' || !(window as any).__TAURI__) return false;
  if (isHomeLibraryHost()) return false;
  return isRemoteConnectorShell();
}

/**
 * Load cover art from home library via Mycelium tunnel proxy (authenticated fetch → blob URL).
 */
export async function getRemoteCoverArt(
  hash: string | null | undefined,
  machineId?: string | null,
): Promise<string | null> {
  const normalized = normalizeCoverHash(hash);
  if (!normalized) return null;

  const cacheKey = `remote:${normalized}:${machineId || ''}`;
  const cached = _cache.get(cacheKey);
  if (cached !== undefined) return cached;

  const existing = _inflight.get(cacheKey);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      const { fetchCloudAuthenticated } = await import('../services/api');
      const qs = machineId ? `?machine_id=${encodeURIComponent(machineId)}` : '';
      const resp = await fetchCloudAuthenticated(`/api/library/remote/cover/${normalized}${qs}`);
      if (!resp.ok) {
        _cache.set(cacheKey, null);
        return null;
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      _cache.set(cacheKey, blobUrl);
      return blobUrl;
    } catch (e) {
      console.warn('[CoverIPC] remote cover fetch failed', normalized.slice(0, 12), e);
      _cache.set(cacheKey, null);
      return null;
    } finally {
      _inflight.delete(cacheKey);
    }
  })();

  _inflight.set(cacheKey, promise);
  return promise;
}

async function invokeIPC<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  return (window as any).__TAURI__.core.invoke(cmd, args);
}

/**
 * Load a single cover by its SHA256/blake3 hash via Tauri IPC.
 * Returns a `data:image/…;base64,…` URL or null.
 */
export async function getCoverArt(
  hash: string | null | undefined,
  context?: { albumId?: string | null; machineId?: string | null },
): Promise<string | null> {
  const normalized = normalizeCoverHash(hash);
  if (!normalized) return null;
  if (!isConnectorAvailable()) return null;

  // Remote Connector shell has no local files — always proxy via Mycelium tunnel.
  if (isRemoteCoverViewerShell()) {
    return getRemoteCoverArt(normalized, context?.machineId ?? null);
  }

  const cached = _cache.get(normalized);
  if (cached !== undefined) return cached;

  const existing = _inflight.get(normalized);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      if (import.meta.env?.DEV) console.debug('[cover-fix] requesting artwork', {
        albumId: context?.albumId ?? null,
        artworkHash: normalized.slice(0, 12),
        source: 'read_cover_art',
      });
      const resp = await invokeIPC<CoverArtResponse | null>('read_cover_art', { hash: normalized });
      if (resp && resp.data) {
        const dataUrl = `data:${resp.mime_type};base64,${resp.data}`;
        if (import.meta.env?.DEV) console.debug('[cover-fix] got cover bytes', {
          artworkHash: normalized.slice(0, 12),
          bytes: resp.data.length,
        });
        _cache.set(normalized, dataUrl);
        return dataUrl;
      }
      console.error('[cover-fix] cover lookup failed', {
        albumId: context?.albumId ?? null,
        artworkHash: normalized.slice(0, 12),
        error: 'read_cover_art returned empty',
      });
      _cache.set(normalized, null);
      return null;
    } catch (e) {
      console.error('[cover-fix] cover lookup failed', {
        albumId: context?.albumId ?? null,
        artworkHash: normalized.slice(0, 12),
        error: e,
      });
      _cache.set(normalized, null);
      return null;
    } finally {
      _inflight.delete(normalized);
    }
  })();

  _inflight.set(normalized, promise);
  return promise;
}

/**
 * Load cover art by album folder path. The Connector extracts it
 * on-demand from embedded audio tags or sidecar images (cover.jpg, etc.).
 */
export async function getCoverArtByPath(albumPath: string | null | undefined): Promise<string | null> {
  if (!albumPath || typeof albumPath !== 'string') return null;
  if (albumPath.startsWith('local:')) return null;
  if (!isConnectorAvailable()) return null;

  const cached = _pathCache.get(albumPath);
  if (cached !== undefined) return cached;

  const key = `path:${albumPath}`;
  const existing = _inflight.get(key);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      const resp = await invokeIPC<CoverArtResponse | null>('read_cover_art_from_path', { albumPath });
      if (resp && resp.data) {
        const dataUrl = `data:${resp.mime_type};base64,${resp.data}`;
        _pathCache.set(albumPath, dataUrl);
        return dataUrl;
      }
      _pathCache.set(albumPath, null);
      return null;
    } catch (e) {
      console.warn('[CoverIPC] path IPC failed for', albumPath, e);
      _pathCache.set(albumPath, null);
      return null;
    } finally {
      _inflight.delete(key);
    }
  })();

  _inflight.set(key, promise);
  return promise;
}

/**
 * Try hash-based lookup first, then fall back to filesystem folder path only.
 */
export async function getCoverArtWithFallback(
  embeddedHash: string | null | undefined,
  folderHash: string | null | undefined,
  albumPath: string | null | undefined,
  context?: { albumId?: string | null; machineId?: string | null },
): Promise<string | null> {
  const emb = normalizeCoverHash(embeddedHash);
  const fol = normalizeCoverHash(folderHash);
  const byHash = await getCoverArt(emb, context) || await getCoverArt(fol, context);
  if (byHash) return byHash;
  const folder = pickAlbumFolderPath({ library_folder_path: albumPath }) ?? (
    albumPath && !albumPath.startsWith('local:') ? albumPath : null
  );
  if (folder) return getCoverArtByPath(folder);
  return null;
}

/**
 * Batch-load covers by hash. Returns a Map<hash, dataUrl|null>.
 */
export async function getCoverArtBatch(
  hashes: (string | null | undefined)[]
): Promise<Map<string, string | null>> {
  const result = new Map<string, string | null>();
  if (!isConnectorAvailable()) return result;

  if (isRemoteCoverViewerShell()) {
    await Promise.all(
      hashes.map(async (h) => {
        const normalized = normalizeCoverHash(h);
        if (!normalized) return;
        const url = await getRemoteCoverArt(normalized, null);
        result.set(normalized, url);
      }),
    );
    return result;
  }

  const needed: string[] = [];
  for (const h of hashes) {
    const normalized = normalizeCoverHash(h);
    if (!normalized) continue;
    const cached = _cache.get(normalized);
    if (cached !== undefined) {
      result.set(normalized, cached);
    } else {
      needed.push(normalized);
    }
  }

  if (needed.length === 0) return result;

  const CHUNK = 32;
  for (let offset = 0; offset < needed.length; offset += CHUNK) {
    const slice = needed.slice(offset, offset + CHUNK);
    try {
      const responses = await invokeIPC<(CoverArtResponse | null)[]>(
        'read_cover_art_batch',
        { hashes: slice },
      );
      for (let i = 0; i < slice.length; i++) {
        const hash = slice[i];
        const resp = responses[i];
        if (resp?.data) {
          const dataUrl = `data:${resp.mime_type};base64,${resp.data}`;
          _cache.set(hash, dataUrl);
          result.set(hash, dataUrl);
        } else {
          _cache.set(hash, null);
          result.set(hash, null);
        }
      }
    } catch (e) {
      console.warn('[CoverIPC] batch failed:', e);
      for (const h of slice) {
        _cache.set(h, null);
        result.set(h, null);
      }
    }
    if (offset + CHUNK < needed.length) {
      await new Promise((r) => setTimeout(r, 80));
    }
  }

  return result;
}

export function isCoverIPCAvailable(): boolean {
  return isConnectorAvailable();
}

export function getCoverFromCache(hash: string | null | undefined): string | null | undefined {
  const normalized = normalizeCoverHash(hash);
  if (!normalized) return undefined;
  return _cache.get(normalized);
}

export function getRemoteCoverFromCache(
  hash: string | null | undefined,
  machineId?: string | null,
): string | null | undefined {
  const normalized = normalizeCoverHash(hash);
  if (!normalized) return undefined;
  const cacheKey = `remote:${normalized}:${machineId || ''}`;
  if (_cache.has(cacheKey)) return _cache.get(cacheKey);
  // Batch prefetch may have stored without machine_id — try that fallback.
  const genericKey = `remote:${normalized}:`;
  if (_cache.has(genericKey)) return _cache.get(genericKey);
  return undefined;
}

/** Drop in-memory cover blobs after wipe/rescan so stale art is not reused. */
export function clearCoverArtCache(): void {
  _cache.clear();
  _pathCache.clear();
  _inflight.clear();
}

export { isLocalLibraryAlbum };
